import type { Meta, StoryObj } from '@storybook/angular';
import { expect, within } from 'storybook/test';
import { ProductDiscountLabelComponent } from '../../../app/content/layout/components/product/product-discount-label/product-discount-label.component';
import { WineProduct, ProductImageBadge } from '../../../app/core/interfaces/generated/graphql';

// Mock base wine product
const createMockWineProduct = (productImageBadge: ProductImageBadge | null): Partial<WineProduct> => ({
  id: '1',
  title: 'Test Wine Product',
  productImageBadge,
});

const meta: Meta<ProductDiscountLabelComponent> = {
  title: 'Molecules/Product/Product Discount Label',
  component: ProductDiscountLabelComponent,
  parameters: {
    docs: {
      description: {
        component: 'Component for displaying product discount badges including campaign discounts with custom colors.',
      },
    },
    layout: 'centered',
  },
  argTypes: {
    product: {
      description: 'The wine product data containing the productImageBadge',
    },
  },
};

export default meta;
type Story = StoryObj<ProductDiscountLabelComponent>;

export const LastBottles: Story = {
  args: {
    product: createMockWineProduct({
      type: 'last_bottles',
      value: null,
    } as ProductImageBadge) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-product-discount-label')).toBeTruthy();
  },
};

export const ShowDiscount: Story = {
  args: {
    product: createMockWineProduct({
      type: 'show_discount',
      value: '25',
    } as ProductImageBadge) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/25/)).toBeInTheDocument();
  },
};

export const CampaignDiscountDefault: Story = {
  args: {
    product: createMockWineProduct({
      type: 'campaign_discount',
      value: '30',
      text: 'TILBUD',
      badge_color: '#FF0000',
      text_color: '#FFFFFF',
    } as ProductImageBadge) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/tilbud/i)).toBeInTheDocument();
  },
};

export const CampaignDiscountBlue: Story = {
  args: {
    product: createMockWineProduct({
      type: 'campaign_discount',
      value: '20',
      text: 'WEEKEND',
      badge_color: '#0066CC',
      text_color: '#FFFFFF',
    } as ProductImageBadge) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/weekend/i)).toBeInTheDocument();
  },
};

export const CampaignDiscountGreen: Story = {
  args: {
    product: createMockWineProduct({
      type: 'campaign_discount',
      value: '15',
      text: 'ECO',
      badge_color: '#00AA44',
      text_color: '#FFFFFF',
    } as ProductImageBadge) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/eco/i)).toBeInTheDocument();
  },
};

export const CampaignDiscountYellow: Story = {
  args: {
    product: createMockWineProduct({
      type: 'campaign_discount',
      value: '40',
      text: 'SUPER SPAR',
      badge_color: '#FFD700',
      text_color: '#000000',
    } as ProductImageBadge) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/super spar/i)).toBeInTheDocument();
  },
};

export const CampaignDiscountNoText: Story = {
  args: {
    product: createMockWineProduct({
      type: 'campaign_discount',
      value: '35',
      text: null,
      badge_color: '#8B0000',
      text_color: '#FFFFFF',
    } as ProductImageBadge) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-product-discount-label')).toBeTruthy();
  },
};

export const CampaignDiscountNoColors: Story = {
  args: {
    product: createMockWineProduct({
      type: 'campaign_discount',
      value: '25',
      text: 'BASIC',
      badge_color: null,
      text_color: null,
    } as ProductImageBadge) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/basic/i)).toBeInTheDocument();
  },
};

export const NoBadge: Story = {
  args: {
    product: createMockWineProduct(null) as WineProduct,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.product-discount-label, .badge')).toBeFalsy();
  },
};